import { createHelia } from 'helia';
import { unixfs } from '@helia/unixfs';
import { IDBBlockstore } from 'blockstore-idb';
import { IDBDatastore } from 'datastore-idb';
import { createLibp2p } from 'libp2p';
import { identify } from '@libp2p/identify';
import { webSockets } from '@libp2p/websockets';
import { webTransport } from '@libp2p/webtransport';
import { webRTC } from '@libp2p/webrtc';
import { bootstrap } from '@libp2p/bootstrap';
import { gossipsub } from '@chainsafe/libp2p-gossipsub';
import { noise } from '@libp2p/noise';
import { yamux } from '@chainsafe/libp2p-yamux';
import { circuitRelayTransport } from '@libp2p/circuit-relay-v2';
import * as MP4Box from 'mp4box';

class IpfsStreamer {
  constructor(options = {}) {
    this.options = {
      relayNodes: [],
      discoveryIntervalMs: 10000,
      peerTimeoutMs: 25000,
      ...options
    };

    this.videoElement = null;
    this.mediaSource = null;
    this.trackBuffers = new Map(); // trackId -> SourceBuffer
    this.queues = new Map(); // trackId -> Array of ArrayBuffers
    this.helia = null;
    this.fs = null;
    this.peerId = null;

    this.currentStreamCid = null;
    this.currentStreamMetadata = null;
    this.activePeers = new Map(); // peerId -> { cid, metadata, timestamp }
    this.discoveryCallbacks = [];
    
    // MP4Box file reference and processing variables
    this.mp4boxFile = null;
    this.bytesFetched = 0;
    this.isFetching = false;
    this.abortController = null;

    // Start initialization asynchronously
    this.initPromise = this._initialize();
  }

  async _initialize() {
    console.log("[IpfsStreamer] Initializing browser P2P nodes...");

    try {
      // 1. Storage setup (IndexedDB)
      const blockstore = new IDBBlockstore('ipfs-streamer-blocks');
      const datastore = new IDBDatastore('ipfs-streamer-data');
      await blockstore.open();
      await datastore.open();

      // Standard Kubo bootstrap nodes (WebSockets secure endpoints with geographically resolved subdomains)
      const bootstrapNodes = [
        '/dns4/ny5.bootstrap.libp2p.io/tcp/443/wss/p2p/QmNnooDu7bfjUE67XLfBYgVat6vNDuGvJhsD6KwWK7v5Db',
        '/dns4/am6.bootstrap.libp2p.io/tcp/443/wss/p2p/QmbLHAnMoJPWSCR5Zhtx6BHJX9KiKNN6tpvbUcqanj75Nb',
        '/dns4/sjc1.bootstrap.libp2p.io/tcp/443/wss/p2p/QmZa1sAxajnQjVM8WjV664sv7EofbUaNfB6XWn6T8moxZq',
        '/dns4/sg1.bootstrap.libp2p.io/tcp/443/wss/p2p/QmcZ31m6G6JHgQE6m3fCh7fymagZGXfT8EHeSgpRE4L7S6'
      ];

      const combinedBootstrappers = [
        ...bootstrapNodes,
        ...(this.options.relayNodes || [])
      ];

      // 2. Setup custom libp2p with browser-compatible transports
      const libp2p = await createLibp2p({
        datastore,
        addresses: {
          listen: [
            '/webrtc', // Enable browser-to-browser direct WebRTC connections
            '/p2p-circuit' // Enable listening via relays
          ]
        },
        connectionManager: {
          maxParallelDials: 15, // Limit parallel connection attempts to prevent hitting browser socket 'Insufficient resources' throttling
          dialTimeout: 10000 // Close unresponsive connection attempts after 10s to free resources
        },
        connectionGater: {
          // Filter out guaranteed-to-fail dials to minimize console noise while maintaining a massive active peer swarm
          denyDialMultiaddr: (multiaddr) => {
            const str = multiaddr.toString();
            // Deny IPv6 addresses to prevent ERR_NAME_NOT_RESOLVED on IPv4-only networks
            if (str.includes('/ip6/')) {
              return true;
            }
            // Deny legacy bare bootstrap.libp2p.io domain (which has no A/AAAA records)
            if (str.includes('/dns4/bootstrap.libp2p.io/')) {
              return true;
            }
            return false;
          }
        },
        transports: [
          webSockets({
            filter: multiaddrs => multiaddrs // Allow all WebSocket addresses (both secure and insecure, local and public)
          }),
          webTransport(),
          webRTC(),
          circuitRelayTransport({
            discoverRelays: 1
          })
        ],
        connectionEncryption: [noise()],
        streamMuxers: [yamux()],
        peerDiscovery: [
          bootstrap({
            list: combinedBootstrappers
          })
        ],
        services: {
          identify: identify(),
          pubsub: gossipsub({
            emitSelf: false,
            gossipIncoming: true,
            fallbackToFloodsub: true
          })
        }
      });

      this.libp2p = libp2p;

      // 3. Create Helia instance sharing the same datastore for identity persistence
      this.helia = await createHelia({
        blockstore,
        datastore,
        libp2p
      });

      // Start the Helia node to trigger all mixin start sequences (including libp2p)
      await this.helia.start();

      this.fs = unixfs(this.helia);
      this.peerId = this.libp2p.peerId.toString();

      console.log(`[IpfsStreamer] Node successfully started. PeerID: ${this.peerId}`);

      // 4. Setup Gossipsub Discovery on topic "global-stream-discovery"
      const topic = "global-stream-discovery";
      
      this.libp2p.services.pubsub.addEventListener('message', (evt) => {
        if (evt.detail.topic === topic) {
          try {
            const rawData = new TextDecoder().decode(evt.detail.data);
            const data = JSON.parse(rawData);
            this._handleDiscoveryMessage(data, evt.detail.from.toString());
          } catch (err) {
            console.warn("[IpfsStreamer] Failed to parse pubsub discovery message:", err);
          }
        }
      });

      // Subscribe to the global stream discovery topic
      this.libp2p.services.pubsub.subscribe(topic);

      // Start the broadcast & peer maintenance loops
      this._startDiscoveryLoops(topic);

    } catch (error) {
      console.error("[IpfsStreamer] Critical initialization failure:", error);
      throw error;
    }
  }

  _startDiscoveryLoops(topic) {
    // 1. Broadcast presence loop (runs every 10 seconds)
    this.broadcastInterval = setInterval(async () => {
      if (this.currentStreamCid) {
        try {
          const payload = {
            peerId: this.peerId,
            cid: this.currentStreamCid,
            metadata: this.currentStreamMetadata || {},
            timestamp: Date.now()
          };
          const data = new TextEncoder().encode(JSON.stringify(payload));
          await this.libp2p.services.pubsub.publish(topic, data);
          console.log(`[IpfsStreamer] Broadcasted active stream: ${this.currentStreamCid}`);
        } catch (err) {
          console.warn("[IpfsStreamer] Error broadcasting discovery state:", err);
        }
      }
    }, this.options.discoveryIntervalMs);

    // 2. Peer maintenance & update callback loop (runs every 5 seconds)
    this.maintenanceInterval = setInterval(() => {
      this._pruneAndNotify();
    }, 5000);
  }

  _handleDiscoveryMessage(data, fromPeerId) {
    if (!data.cid || !fromPeerId) return;

    // Save or update peer info
    this.activePeers.set(fromPeerId, {
      cid: data.cid,
      metadata: data.metadata || {},
      timestamp: Date.now()
    });

    // Notify immediately on updates to keep the dashboard snappy
    this._pruneAndNotify();
  }

  _pruneAndNotify() {
    const now = Date.now();

    // Prune stale peers
    for (const [peerId, peerData] of this.activePeers.entries()) {
      if (now - peerData.timestamp > this.options.peerTimeoutMs) {
        this.activePeers.delete(peerId);
      }
    }

    // Always aggregate and notify to maintain precise counts
    const streamsMap = new Map();
    for (const [peerId, data] of this.activePeers.entries()) {
      const { cid, metadata } = data;
      if (!streamsMap.has(cid)) {
        streamsMap.set(cid, {
          cid,
          title: metadata.title || 'Untitled Stream',
          metadata,
          activeViewers: 0,
          peers: []
        });
      }
      const streamInfo = streamsMap.get(cid);
      streamInfo.activeViewers += 1;
      streamInfo.peers.push(peerId);
    }

    const activeStreamsList = Array.from(streamsMap.values());
    this.discoveryCallbacks.forEach(cb => {
      try {
        cb(activeStreamsList);
      } catch (err) {
        console.error("[IpfsStreamer] Error executing discovery callback:", err);
      }
    });
  }

  onDiscoveryUpdate(callback) {
    if (typeof callback === 'function') {
      this.discoveryCallbacks.push(callback);
      // Immediately run the callback with the current active list
      this._pruneAndNotify();
    }
  }

  async addVideo(file, progressCallback = null) {
    await this.initPromise;
    console.log("[IpfsStreamer] Importing video file to local P2P blockstore...", file.name, file.size);

    let uint8Array;
    if (file instanceof File || file instanceof Blob) {
      const arrayBuffer = await file.arrayBuffer();
      uint8Array = new Uint8Array(arrayBuffer);
    } else if (file instanceof Uint8Array) {
      uint8Array = file;
    } else {
      throw new Error("[IpfsStreamer] Invalid input. Must be a File, Blob, or Uint8Array.");
    }

    const cid = await this.fs.addBytes(uint8Array, {
      onProgress: (evt) => {
        if (progressCallback && evt.type === 'unixfs:add:progress') {
          const percent = (Number(evt.detail) / uint8Array.length) * 100;
          progressCallback(percent, Number(evt.detail), uint8Array.length);
        }
      }
    });

    const cidStr = cid.toString();
    console.log(`[IpfsStreamer] Video successfully added to local blockstore. CID: ${cidStr}`);
    return cidStr;
  }

  attachTo(videoElement) {
    if (!videoElement) {
      throw new Error("[IpfsStreamer] Invalid video element provided.");
    }
    this.videoElement = videoElement;
    console.log("[IpfsStreamer] Attached to video element.");
  }

  async loadStream(cid, metadata = {}) {
    await this.initPromise;
    console.log(`[IpfsStreamer] Loading stream CID: ${cid}`, metadata);

    // 1. Cleanup any active playback
    this._cleanupStream();

    this.currentStreamCid = cid;
    this.currentStreamMetadata = metadata;

    // Reset offsets and queues
    this.bytesFetched = 0;
    this.queues.clear();
    this.trackBuffers.clear();
    this.isFetching = true;
    this.abortController = new AbortController();

    // 2. Initialize MediaSource
    this.mediaSource = new MediaSource();
    this.videoElement.src = URL.createObjectURL(this.mediaSource);

    // Wait for the MediaSource to be opened
    this.mediaSource.addEventListener('sourceopen', () => {
      this._onMediaSourceOpen(cid);
    }, { once: true });
  }

  _cleanupStream() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    this.isFetching = false;

    if (this.videoElement) {
      this.videoElement.pause();
      try {
        this.videoElement.removeAttribute('src');
        this.videoElement.load();
      } catch (e) {}
    }

    if (this.mediaSource) {
      if (this.mediaSource.readyState === 'open') {
        try {
          this.mediaSource.endOfStream();
        } catch (e) {}
      }
      this.mediaSource = null;
    }

    this.trackBuffers.clear();
    this.queues.clear();
    this.bytesFetched = 0;

    if (this.mp4boxFile) {
      try {
        this.mp4boxFile.flush();
      } catch (e) {}
      this.mp4boxFile = null;
    }
  }

  _onMediaSourceOpen(cid) {
    this.mp4boxFile = MP4Box.createFile();

    this.mp4boxFile.onReady = (info) => {
      console.log("[IpfsStreamer] MP4Box file parsed and ready. Track Info:", info);

      info.tracks.forEach((track) => {
        // Build codec string
        const mime = `video/mp4; codecs="${track.codec}"`;
        
        console.log(`[IpfsStreamer] Creating SourceBuffer for mime: ${mime}`);
        if (MediaSource.isTypeSupported(mime)) {
          try {
            const sb = this.mediaSource.addSourceBuffer(mime);
            this.trackBuffers.set(track.id, sb);

            // Listen to updateend to run the queue
            sb.addEventListener('updateend', () => {
              this._processBufferQueue(track.id, sb);
            });

            // Set segmentation options on MP4Box
            // Generate segments based on 100 samples (roughly 2-4 seconds of stream chunks)
            this.mp4boxFile.setSegmentOptions(track.id, sb, { nbSamples: 100 });
          } catch (err) {
            console.error(`[IpfsStreamer] Failed to add SourceBuffer for track ${track.id}:`, err);
          }
        } else {
          console.error(`[IpfsStreamer] Mime type is not supported by browser: ${mime}`);
        }
      });

      // Initialize segmentation with MP4Box
      let initSegments = this.mp4boxFile.initializeSegmentation('per-track');
      
      // Robust fallback handling: if it's not an array, handle 'combined' mode safely
      if (!Array.isArray(initSegments)) {
        console.warn("[IpfsStreamer] initializeSegmentation did not return an array. Falling back to combined handling.", initSegments);
        if (initSegments && initSegments.buffer) {
          if (initSegments.tracks) {
            initSegments.tracks.forEach((trackInfo) => {
              const sb = trackInfo.user;
              this._pushToBufferQueue(trackInfo.id, sb, initSegments.buffer);
            });
          }
          initSegments = []; // clear to skip standard array loop
        } else if (initSegments) {
          initSegments = [initSegments]; // wrap just in case
        } else {
          initSegments = [];
        }
      }

      initSegments.forEach((seg) => {
        if (seg && seg.user) {
          const sb = seg.user; // seg.user is the SourceBuffer we mapped in setSegmentOptions
          this._pushToBufferQueue(seg.id, sb, seg.buffer);
        }
      });

      // Start processing extraction
      this.mp4boxFile.start();
    };

    this.mp4boxFile.onSegment = (id, sb, buffer, sampleNumber, last) => {
      console.log(`[IpfsStreamer] MP4Box segment received. Track ID: ${id}, size: ${buffer.byteLength}, last: ${last}`);
      this._pushToBufferQueue(id, sb, buffer);
    };

    this.mp4boxFile.onError = (e) => {
      console.error("[IpfsStreamer] MP4Box error:", e);
    };

    // Start fetching from UnixFS!
    this._fetchIpfsUnixFs(cid);
  }

  async _fetchIpfsUnixFs(cid) {
    try {
      console.log(`[IpfsStreamer] Requesting UnixFS stream from IPFS swarm for CID: ${cid}`);
      
      const signal = this.abortController.signal;
      const chunks = [];
      let totalLength = 0;

      for await (const chunk of this.fs.cat(cid, { signal })) {
        if (!this.isFetching) break;
        chunks.push(chunk);
        totalLength += chunk.byteLength;
        this.bytesFetched = totalLength;
        console.log(`[IpfsStreamer] Fetched block of size ${chunk.byteLength}. Total so far: ${totalLength}`);
      }

      console.log(`[IpfsStreamer] Finished fetching all blocks from IPFS. Total size: ${totalLength} bytes.`);
      if (!this.isFetching) return;

      // Concatenate all chunks into a single Uint8Array
      const fullBuffer = new Uint8Array(totalLength);
      let offset = 0;
      for (const chunk of chunks) {
        fullBuffer.set(chunk, offset);
        offset += chunk.byteLength;
      }

      // Convert to ArrayBuffer
      const arrayBuffer = fullBuffer.buffer;
      arrayBuffer.fileStart = 0;

      // Feed to MP4Box in one synchronous operation
      console.log("[IpfsStreamer] Feeding full combined buffer to MP4Box...");
      this.mp4boxFile.appendBuffer(arrayBuffer);

      console.log("[IpfsStreamer] Flushing MP4Box...");
      this.mp4boxFile.flush();

    } catch (err) {
      if (err.name === 'AbortError') {
        console.log("[IpfsStreamer] Fetching stream aborted.");
      } else {
        console.error("[IpfsStreamer] Error streaming from IPFS UnixFS:", err);
      }
    }
  }

  _pushToBufferQueue(trackId, sourceBuffer, buffer) {
    if (!this.queues.has(trackId)) {
      this.queues.set(trackId, []);
    }

    const queue = this.queues.get(trackId);
    queue.push(buffer);

    this._processBufferQueue(trackId, sourceBuffer);
  }

  _processBufferQueue(trackId, sourceBuffer) {
    const queue = this.queues.get(trackId);
    if (!queue || queue.length === 0) {
      this._checkPlaybackEnd();
      return;
    }
    
    const sb = sourceBuffer || this.trackBuffers.get(trackId);
    if (!sb) return;

    // If the SourceBuffer is currently busy updating, do not append.
    // It will trigger 'updateend' when done, which calls this function.
    if (sb.updating) return;

    const buffer = queue.shift();
    try {
      sb.appendBuffer(buffer);
      console.log(`[IpfsStreamer] Appended segment to SourceBuffer for track ${trackId}. Queue length: ${queue.length}`);
    } catch (err) {
      console.error(`[IpfsStreamer] Error appending buffer to SourceBuffer for track ${trackId}:`, err);
      // Put it back to try again
      queue.unshift(buffer);
    }
  }

  _checkPlaybackEnd() {
    if (this.mediaSource && this.mediaSource.readyState === 'open') {
      let allEmpty = true;
      for (const queue of this.queues.values()) {
        if (queue.length > 0) allEmpty = false;
      }
      let allBuffersNotUpdating = true;
      for (const sb of this.trackBuffers.values()) {
        if (sb.updating) allBuffersNotUpdating = false;
      }
      
      // If we finished fetching from IPFS and all queues are processed, close the stream
      if (allEmpty && allBuffersNotUpdating && !this.isFetching) {
        try {
          this.mediaSource.endOfStream();
          console.log("[IpfsStreamer] MediaSource playback finished. Ended stream.");
        } catch (e) {}
      }
    }
  }
}

// Bind directly to window to support simple static page imports
if (typeof window !== 'undefined') {
  window.IpfsStreamer = IpfsStreamer;
}

export default IpfsStreamer;
